import React from "react"
import { toast } from "react-toastify"
import Popup from "../Popup/Popup"
import Button from "../Button/Button"
import ToastInfo from "../ToastContents/ToastInfo"
import { downloadRecoveryCodesPdf } from "../../helpers/recoveryCodesPdf"
import styles from "./RecoveryCodesModal.module.scss"

export { downloadRecoveryCodesPdf }

export const copyRecoveryCodes = async (codes) => {
  const text = [
    "BRIDGE 2 TECHNOLOGIES MFA Recovery Codes",
    "Each code can be used only once.",
    "",
    ...codes,
  ].join("\n")

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.position = "absolute"
  textarea.style.left = "-9999px"
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand("copy")
  document.body.removeChild(textarea)
}

const formatRecoveryCodeDisplay = (code) => {
  const digits = String(code || "").replace(/\D/g, "")
  if (digits.length === 16) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12)}`
  }
  return String(code)
}

const RecoveryCodesModal = ({
  isOpen,
  onClose,
  codes = [],
  loading = false,
  title = "Your Recovery Codes",
}) => {
  const [copying, setCopying] = React.useState(false)

  if (!isOpen) return null

  const handleCopy = async () => {
    if (!codes.length) return
    setCopying(true)
    try {
      await copyRecoveryCodes(codes)
      toast.success(<ToastInfo>Recovery codes copied.</ToastInfo>, {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      setCopying(false)
    }
  }

  return (
    <Popup
      width={620}
      title={title}
      isCrossIcon
      closeAction={onClose}
      closeByOutsideClick
      className={styles.modal}
      childrenOverflowScroll
    >
      <div className={styles.content}>
        <div className={styles.hero}>
          <div className={styles.heroIcon} aria-hidden="true">
            &#128737;
          </div>
          <div className={styles.heroText}>
            <p className={styles.description}>
              Store these codes somewhere safe. Each code works once if you
              cannot access your email during MFA login.
            </p>
            <p className={styles.meta}>
              {loading
                ? "Loading your recovery codes..."
                : `${codes.length} recovery code${codes.length === 1 ? "" : "s"} available`}
            </p>
          </div>
        </div>

        {!loading && codes.length > 0 && (
          <p className={styles.warning}>
            Save or download these codes now. Regenerating a new set will
            invalidate all codes shown here.
          </p>
        )}

        {loading ? (
          <div className={styles.loadingWrap}>
            <span className={styles.spinner} aria-hidden="true" />
            Loading recovery codes...
          </div>
        ) : codes.length ? (
          <ul className={styles.codeGrid}>
            {codes.map((code, index) => (
              <li key={code} className={styles.codeItem}>
                <span className={styles.codeIndex}>{index + 1}</span>
                <span className={styles.codeValue}>
                  {formatRecoveryCodeDisplay(code)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>
            No recovery codes are available yet. Close this dialog and click
            Regenerate Codes to create a new set.
          </p>
        )}

        <div className={styles.actions}>
          <Button
            className={styles.actionPrimary}
            onClick={onClose}
            disabled={loading}
          >
            Close
          </Button>
          <Button
            buttonStyle="outlinedBlack"
            className={styles.actionSecondary}
            onClick={() => downloadRecoveryCodesPdf(codes)}
            disabled={!codes.length || loading}
          >
            Download PDF
          </Button>
          <Button
            buttonStyle="outlinedBlack"
            className={styles.actionSecondary}
            onClick={handleCopy}
            disabled={!codes.length || copying || loading}
          >
            {copying ? "Copying..." : "Copy Codes"}
          </Button>
        </div>
      </div>
    </Popup>
  )
}

export default RecoveryCodesModal
